import { useMemo } from "react";
import { LockClosedIcon, CheckIcon } from "@heroicons/react/24/outline";
import { getWidgetById } from "@/utils/widgets";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import type { Portfolio } from "@/types/Portfolio";
import { CARD, SECTION_LABEL } from "../ui/theme";
import TermChips from "./TermChips";
import type { ProvisioningPlan } from "./types";

interface PlanPreviewProps {
  plan: ProvisioningPlan;
  /** Ticker chips are editable — the hallucination backstop. */
  onChange: (plan: ProvisioningPlan) => void;
  onUpgradeClick?: () => void;
  /**
   * Library lists to resolve `lib:` refs against, instead of the signed-in
   * user's own fetch.
   *
   * Only the guided demo passes this. It renders recorded plans to logged-out
   * visitors, for whom usePortfoliosCRUD returns nothing — so every "Reused
   * from the Newsquawk library" row would come out empty, which reads as the
   * plan citing lists that don't exist. The recorder resolves the refs at
   * record time and the fixture carries the contents.
   */
  libraryOverride?: Portfolio[];
}

/** Small labelled group with a hairline label above it. */
function Group({
  label,
  children,
  index,
}: {
  label: string;
  children: React.ReactNode;
  index: number;
}) {
  return (
    <section
      className="nq-onb-rise"
      style={{ ["--nq-i" as string]: index }}
    >
      <h3 className={SECTION_LABEL}>{label}</h3>
      <div className="mt-2 space-y-1.5">{children}</div>
    </section>
  );
}

/** A created resource: name + one-line rationale, no paragraph. */
function ResourceRow({
  name,
  detail,
  children,
}: {
  name: string;
  detail: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`${CARD} px-3.5 py-3`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-semibold text-gray-100">{name}</span>
        <span className="text-xs text-gray-500">{detail}</span>
      </div>
      {children}
    </div>
  );
}

export default function PlanPreview({
  plan,
  onChange,
  onUpgradeClick,
  libraryOverride,
}: PlanPreviewProps) {
  // The hook still runs unconditionally (rules of hooks); the override just
  // wins. Logged out it resolves to an empty list anyway, so this costs
  // nothing in the demo case.
  const { portfolios: ownPortfolios } = usePortfoliosCRUD();
  const portfolios = libraryOverride ?? ownPortfolios;

  /**
   * Every library ref the plan leans on, from wherever it can appear.
   *
   * `reused_refs` is the plan's own declaration and the only source that
   * works in watchlist mode, which emits neither a dashboard nor (usually) a
   * query. The dashboard and query refs are still swept because an onboarding
   * plan puts them there directly, and a list used on a widget is reused
   * whether or not the model also remembered to declare it.
   */
  const { libNames, libQueryNames } = useMemo(() => {
    const lists = new Set<string>();
    const filters = new Set<string>();
    const note = (ref: string) => {
      if (ref.startsWith("libq:")) filters.add(ref.slice(5));
      else if (ref.startsWith("lib:")) lists.add(ref.slice(4));
    };
    plan.reused_refs?.forEach(note);
    for (const query of plan.headline_queries) {
      [...query.portfolio_refs, ...query.tags_watchlist_refs].forEach(note);
    }
    for (const widget of plan.dashboard_tab?.widgets ?? []) {
      [...widget.highlight_refs, ...widget.filter_refs].forEach(note);
    }
    return { libNames: lists, libQueryNames: [...filters] };
  }, [plan.reused_refs, plan.headline_queries, plan.dashboard_tab]);

  /** Reused lists resolved to their real contents, so terms are visible. */
  const reusedLists = useMemo<Portfolio[]>(
    () =>
      libNames.size === 0
        ? []
        : portfolios.filter(
            (p: Portfolio) => p.owner_type === "NEWSQUAWK" && libNames.has(p.name)
          ),
    [libNames, portfolios]
  );

  const reusedFilters = libQueryNames;

  const removeTicker = (ref: string, ticker: string) =>
    onChange({
      ...plan,
      portfolios: plan.portfolios.map((p) =>
        p.ref === ref ? { ...p, tickers: p.tickers.filter((t) => t !== ticker) } : p
      ),
    });

  const removeKeyword = (ref: string, keyword: string) =>
    onChange({
      ...plan,
      portfolios: plan.portfolios.map((p) =>
        p.ref === ref ? { ...p, keywords: p.keywords.filter((k) => k !== keyword) } : p
      ),
    });

  let i = 0;

  return (
    <div className="space-y-5">
      {/* The verdict: one line + scannable bullets. No prose. */}
      <div className="nq-onb-rise" style={{ ["--nq-i" as string]: i++ }}>
        <p className="text-base font-semibold text-gray-100 sm:text-lg">
          {plan.headline}
        </p>
        {plan.bullets.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {plan.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2.5 text-[13px] leading-snug text-gray-300">
                <CheckIcon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-squawk-blue" />
                {bullet}
              </li>
            ))}
          </ul>
        )}
      </div>

      {(reusedLists.length > 0 || reusedFilters.length > 0) && (
        <Group label="Reused from the Newsquawk library" index={i++}>
          {reusedLists.map((list) => (
            <div key={list.id} className={`${CARD} px-3.5 py-3`}>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-semibold text-gray-100">
                  {list.name}
                </span>
                <span className="text-xs text-gray-500">
                  {(list.tickers?.length ?? 0) + (list.keywords?.length ?? 0)} terms
                  · already curated by the desk
                </span>
              </div>
              <TermChips tickers={list.tickers} keywords={list.keywords} />
            </div>
          ))}
          {reusedFilters.map((name) => (
            <div key={name} className={`${CARD} px-3.5 py-3`}>
              <span className="text-sm font-semibold text-gray-100">{name}</span>
              <span className="ml-2 text-xs text-gray-500">Newsquawk watchlist</span>
            </div>
          ))}
        </Group>
      )}

      {plan.portfolios.length > 0 && (
        <Group label="Watchlists we'll create" index={i++}>
          {plan.portfolios.map((portfolio) => (
            <ResourceRow
              key={portfolio.ref}
              name={portfolio.name}
              detail={portfolio.rationale}
            >
              <TermChips
                tickers={portfolio.tickers}
                keywords={portfolio.keywords}
                onRemoveTicker={(t) => removeTicker(portfolio.ref, t)}
                onRemoveKeyword={(k) => removeKeyword(portfolio.ref, k)}
              />
            </ResourceRow>
          ))}
        </Group>
      )}

      {plan.tags_watchlists.length > 0 && (
        <Group label="Topics" index={i++}>
          {plan.tags_watchlists.map((watchlist) => (
            <ResourceRow
              key={watchlist.ref}
              name={watchlist.name}
              detail={watchlist.rationale}
            />
          ))}
        </Group>
      )}

      {plan.headline_queries.length > 0 && (
        <Group label="Combined watchlists" index={i++}>
          {plan.headline_queries.map((query) => (
            <ResourceRow key={query.ref} name={query.name} detail={query.rationale} />
          ))}
        </Group>
      )}

      {plan.dashboard_tab && (
        <Group label={`Dashboard · ${plan.dashboard_tab.title}`} index={i++}>
          <div className="flex flex-wrap gap-1.5">
            {plan.dashboard_tab.widgets.map((widget, index) => {
              const registryWidget = getWidgetById(widget.widget_id);
              return (
                <span
                  key={`${widget.widget_id}-${index}`}
                  className="inline-flex items-center gap-1.5 rounded border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-200"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-squawk-blue" />
                  {registryWidget?.displayName ?? registryWidget?.name ?? widget.widget_id}
                </span>
              );
            })}
          </div>
        </Group>
      )}

      {plan.tier_suggestions.length > 0 && (
        <section
          className="nq-onb-rise rounded-md border border-amber-500/30 bg-amber-500/10 px-3.5 py-3"
          style={{ ["--nq-i" as string]: i++ }}
        >
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-400/90">
            <LockClosedIcon className="h-3 w-3" />
            Also on higher plans
          </div>
          <ul className="mt-2 space-y-1">
            {plan.tier_suggestions.map((suggestion) => (
              <li key={suggestion.feature} className="text-[13px] leading-snug text-amber-100/70">
                <span className="font-medium text-amber-100/90">{suggestion.feature}</span>
                {suggestion.note ? ` — ${suggestion.note}` : null}
              </li>
            ))}
          </ul>
          {onUpgradeClick && (
            <button
              type="button"
              onClick={onUpgradeClick}
              className="mt-2 cursor-pointer border-none bg-transparent p-0 text-xs font-semibold text-amber-400 underline hover:text-amber-300"
            >
              See plans
            </button>
          )}
        </section>
      )}
    </div>
  );
}
